import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { fetchPortalMe } from '../portalApi';
import { PORTAL_OPERATIONAL_TYPES } from '../../../../lib/portalScope';
import { useClickOutside } from '../../../../lib/useClickOutside';
import { isGroupScope } from '../../../../lib/portalReport/groupScope';
import {
  isLocationSelected,
  locationHeading,
  locationSaveState,
  toggleLocation,
  type LocationSelection,
  type PortalNavSelection,
} from '../../../../lib/portalReport/locationSelection';

// CLIENT PORTAL — the location multi-select (§9, 2026-09-03).
//
// Replaces the static "2 locations" heading: tick the locations to include.
//
// ⚠️⚠️ NOTHING HERE IS A SECURITY BOUNDARY, and that is worth saying out loud
// because it is the sort of component that looks like one. The browser can send
// any ids it likes; the SERVER resolves the granted scope from the contact's
// account links, INTERSECTS the request with it, and 404s anything outside —
// see `selectManyWithinScope` and the route tests. This is a convenience over
// that, not a gate in front of it.
//
// ⚠️⚠️ IT LISTS THE FULL SCOPE, FROM `/me` — never the report payload, whose
// `accounts` is POST-NARROWING and would collapse to the current selection the
// moment a client used the picker, leaving no way back. The same reason the
// left nav reads `/me`, and it SHARES that query key so it costs no extra
// request.

export function LocationPicker({
  selected,
  navSelection,
  navDrillDown,
  onChange,
}: {
  /** ⚠️ `null` = no filter (every location); `[]` = filtered to nothing. */
  selected: LocationSelection;
  /**
   * What the report is ACTUALLY narrowed to, from the payload (§B, 2026-09-03).
   *
   * ⚠️⚠️ IT WINS OVER `selected` FOR THE HEADING, and that is the bug fix. The
   * heading used to be `locationSelectionLabel(selected, …)` alone, and
   * `selected` comes from the `accounts` URL parameter — empty on a nav
   * drill-down, which writes `account`. `locationSelectionLabel(null, …)` then
   * returned "All 6 locations" and printed it over one club's report. The
   * report narrowed correctly; only its name was wrong.
   *
   * ⚠️ Resolved by the caller through the SHARED `resolvePortalNavSelection`, so
   * the heading and the nav's underline cannot disagree.
   */
  navSelection: PortalNavSelection;
  /**
   * ⚠️⚠️ THE REPORT WAS REACHED BY DRILLING INTO ONE CLUB FROM THE NAV (§1,
   * 2026-09-03), so this page HAS no picker — only its heading.
   *
   * THE DEFECT: the heading read "Snap Fitness Banksia Grove" while all six
   * checkboxes were ticked. The boxes read `isLocationSelected(selected, id)`,
   * `selected` is the `accounts` parameter, a nav drill-down writes `account`
   * instead — and `null` means "no filter", which is every box ticked. So the
   * control contradicted the heading directly above it, and a client trusting
   * the boxes would have thought they were looking at the whole group.
   *
   * ⚠️⚠️ IT IS THE `account` PARAMETER, NOT `navSelection.kind === 'account'`.
   * See `ReportState.navDrillDown` for why that distinction is load-bearing:
   * gating on the resolved selection hides the picker the moment somebody picks
   * ONE club through it, which traps them there.
   */
  navDrillDown: boolean;
  onChange: (ids: LocationSelection) => void;
}) {
  const [open, setOpen] = useState(false);
  /**
   * ⚠️⚠️ THE DRAFT (§2, 2026-09-03). Every tick used to commit immediately, so
   * narrowing six clubs to two meant four report fetches and four intermediate
   * reports — including the empty one, when the last tick happened to come off
   * before the first went on.
   *
   * ⚠️ SEEDED ON OPEN, IN THE CLICK HANDLER — never in an effect. An effect
   * would re-seed whenever `selected` changed, and the whole point is that the
   * draft is the one thing `selected` does not follow.
   */
  const [draft, setDraft] = useState<LocationSelection>(selected);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const me = useQuery({ queryKey: ['portal-me'], queryFn: fetchPortalMe, retry: false });

  const locations = (me.data?.accounts ?? []).filter((a) =>
    PORTAL_OPERATIONAL_TYPES.includes(a.accountType),
  );

  /**
   * Close without committing, and put focus back where it started.
   *
   * ⚠️ CANCEL IS ALSO WHAT CLICKING OUTSIDE DOES (architect's default). A
   * dismissal is not an instruction, and a click-outside that COMMITTED would
   * make an accidental click on the page silently change a client's report.
   */
  const cancel = (): void => {
    setOpen(false);
    triggerRef.current?.focus();
  };
  useClickOutside([ref], cancel, open);

  // ⚠️ ONE LOCATION IS NOT A CHOICE, and neither is a nav drill-down: both are
  // pages where every state of the control would show the same report. They get
  // the heading and nothing else — the same judgement the left nav makes when
  // it omits "All locations" above a list of one.
  //
  // ⚠️ THE HEADING STAYS. "Hidden" means the CONTROL, not the club's name: this
  // is the only thing on the page that names what you are looking at, and §B
  // shipped `locationHeading` precisely so a drill-down could name its club.
  // ⚠️ `isGroupScope` — the shared rule (groupScope.ts), not a fourth copy of
  // `length <= 1`. The `navDrillDown` half stays separate: that flag answers
  // WHICH PARAMETER produced the selection, which `PortalNavSelection` is lossy
  // about, and it is not a question about the scope's size.
  if (navDrillDown || !isGroupScope(locations)) {
    return (
      <p className="rpt-club rpt-display">
        {!isGroupScope(locations)
          ? (locations[0]?.name ?? '')
          : locationHeading(navSelection, selected, locations)}
      </p>
    );
  }

  // ⚠️ THE RULES ARE PURE AND LIVE NEXT DOOR (locationSelection.ts). Asserting
  // them through a rendered picker needs jsdom and CLICKS, and this repo sits
  // one jsdom file from the documented 5s-timeout cliff — so the component holds
  // the markup and the module holds the decisions.
  const allIds = locations.map((l) => l.id);
  // ⚠️⚠️ THE TRIGGER SHOWS THE APPLIED SELECTION, NEVER THE DRAFT. A label that
  // followed the draft would announce a narrowing that has not happened, over a
  // report that still shows everything — and after Cancel it would have to be
  // walked back.
  const label = locationHeading(navSelection, selected, locations);
  const save = locationSaveState(draft, selected);

  return (
    <div
      className="rpt-locpick"
      ref={ref}
      // ⚠️ ESCAPE CANCELS from anywhere inside, including the trigger — the
      // panel's controls are all descendants, so one handler covers both.
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          cancel();
        }
      }}
    >
      <button
        type="button"
        ref={triggerRef}
        className="rpt-club rpt-display rpt-locpick-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          // ⚠️ SEED FROM THE APPLIED SELECTION EVERY TIME IT OPENS, so a
          // cancelled edit leaves nothing behind for the next opening.
          if (!open) setDraft(selected);
          setOpen((o) => !o);
        }}
      >
        {label}
        <span aria-hidden="true" className="rpt-locpick-caret">
          ▾
        </span>
      </button>
      {open && (
        // ⚠️⚠️ `role="listbox"` IS LOAD-BEARING BEYOND ARIA: `.kyck-portal
        // [role='listbox']` is what gives this panel its dark ink (index.css).
        // Before that rule the panel measured 1.15:1 — invisible. Changing the
        // role would take the contrast fix with it, silently.
        <div className="rpt-locpick-panel" role="listbox" aria-label="Locations">
          <div className="rpt-locpick-actions">
            {/* ⚠️ "All" sets NULL, not the full list — see `toggleLocation`. It
                mutates the DRAFT like every other control here. */}
            <button type="button" onClick={() => setDraft(null)}>
              All
            </button>
            <button type="button" onClick={() => setDraft([])}>
              None
            </button>
          </div>
          <div className="rpt-locpick-list">
            {locations.map((l) => (
              <label key={l.id} className="rpt-locpick-row">
                <input
                  type="checkbox"
                  checked={isLocationSelected(draft, l.id)}
                  onChange={() => setDraft(toggleLocation(draft, l.id, allIds))}
                />
                <span>{l.name}</span>
              </label>
            ))}
          </div>
          <div className="rpt-locpick-footer">
            {/* ⚠️ THE REASON RENDERS BESIDE THE DISABLED BUTTON, not as a
                toast: "Choose at least one location" after pressing None is the
                one refusal here nothing else on screen explains. */}
            {save.reason && <span className="rpt-locpick-reason">{save.reason}</span>}
            <button type="button" className="rpt-locpick-cancel" onClick={cancel}>
              Cancel
            </button>
            <button
              type="button"
              className="rpt-locpick-save"
              disabled={!save.canSave}
              onClick={() => {
                onChange(draft);
                setOpen(false);
                triggerRef.current?.focus();
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
